'use client'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { formatDateTime, getCountryCodeFromLocale, getLocalizedValue } from '@/lib/utils'
import {
  MoreHorizontalIcon,
  EyeIcon,
  EditIcon,
  Trash2Icon,
  CalendarIcon,
  HashIcon,
  SquarePlusIcon,
  SquareMenuIcon,
  KeyIcon,
  ArrowUpRightIcon,
  TablePropertiesIcon,
  RefreshCwIcon
} from 'lucide-react'
import Flag from 'react-flagkit'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import {
  LoadingState,
  SortableHeader,
  DeleteConfirmationDialog,
  PropertiesFilters,
  TableSortActions
} from '@/components/partials'
import { PropertiesFilterParams } from '@/types/property'
import { useProperties, useDeleteProperty, useBulkDeleteProperties, useSortProperty } from '@/hooks'
import { usePropertiesStore, useMetaStore, useMeStore } from '@/store'
import { hasPermission } from '@/lib/permissions'
import { SortEnum, SortOrderEnum } from '@/enums'
import { BulkDeleteResponse } from '@/types/general'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteMessageId, setDeleteMessageId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)

  const { mutate: sortProperty, isPending: isSorting } = useSortProperty()

  const selectedMessageIds = usePropertiesStore(state => state.selectedPropertyIds)
  const toggleMessageSelection = usePropertiesStore(state => state.togglePropertySelection)
  const selectAllMessages = usePropertiesStore(state => state.selectAllProperties)
  const deselectAllMessages = usePropertiesStore(state => state.deselectAllProperties)

  const filters = useMemo<PropertiesFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.ASC,
      sortBy: params.get('sortBy') || 'order',
      q: params.get('q') || '',
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])

  const { metaState } = useMetaStore()
  const { meState } = useMeStore()
  const currentUser = meState.response
  const canCreate = hasPermission(currentUser, 'PROPERTIES', 'CREATE')
  const canShow = hasPermission(currentUser, 'PROPERTIES', 'SHOW')
  const canUpdate = hasPermission(currentUser, 'PROPERTIES', 'UPDATE')
  const canDelete = hasPermission(currentUser, 'PROPERTIES', 'DELETE')
  const defaultLocale = metaState.response?.generalSettings.adminDefaultLocale
  const { data: properties, isLoading, error, refetch, isFetching } = useProperties(filters)
  const { mutate: deleteMessageMutate, isPending: isDeleting } = useDeleteProperty()
  const { mutate: bulkDeleteMessagesMutate, isPending: isBulkDeleting } = useBulkDeleteProperties()

  // URL params handling
  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString())
    const newFilters: PropertiesFilterParams = {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.ASC,
      sortBy: params.get('sortBy') || 'order'
    }

    if (params.get('q')) {
      newFilters.q = params.get('q') || ''
    }
    if (params.get('createdAtStart')) {
      newFilters.createdAtStart = params.get('createdAtStart') || undefined
    }
    if (params.get('createdAtEnd')) {
      newFilters.createdAtEnd = params.get('createdAtEnd') || undefined
    }
    if (params.get('updatedAtStart')) {
      newFilters.updatedAtStart = params.get('updatedAtStart') || undefined
    }
    if (params.get('updatedAtEnd')) {
      newFilters.updatedAtEnd = params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])

  // Update URL when filters change
  const updateUrlParams = (newFilters: Partial<PropertiesFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value.toString())
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<PropertiesFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/products/properties')
  }

  const handleSort = (sortBy: string, sort: SortEnum) => {
    handleFiltersChange({ sortBy, sort, page: 1 })
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleView = (messageId: string) => {
    router.push(`/products/properties/${messageId}`)
  }

  const handleEdit = (messageId: string) => {
    router.push(`/products/properties/${messageId}/edit`)
  }

  const handleDelete = (messageId: string) => {
    setDeleteMessageId(messageId)
  }

  const handleSortOrder = (id: string, type: SortOrderEnum) => {
    sortProperty(
      { propertyId: id, type: type },
      {
        onSuccess: async updatedProperty => {
          queryClient.setQueryData(['property', id], updatedProperty)
          await queryClient.invalidateQueries({ queryKey: ['properties'] })
          toast.success(t('sorted_successfully'))
        },
        onError: error => {
          console.error('Error sorting property:', error)
          toast.error(t('failed_to_sort'))
        }
      }
    )
  }

  const confirmDeleteMessage = () => {
    if (deleteMessageId) {
      deleteMessageMutate(deleteMessageId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['properties'] })
          toast.success(t('deleted_successfully'))
          setDeleteMessageId(null)
        },
        onError: error => {
          console.error('Error deleting property:', error)
          toast.error(t('error_deleting'))
          setDeleteMessageId(null)
        }
      })
    }
  }

  const handleBulkDelete = () => {
    if (selectedMessageIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmBulkDeleteMessages = () => {
    if (selectedMessageIds.length > 0) {
      bulkDeleteMessagesMutate(selectedMessageIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['properties'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response) {
            if (error.response.status === 422) {
              toast.error(error.response.data?.items?.ids || t('error_deleting'))
            } else {
              toast.error(t('error_deleting'))
            }
          } else {
            toast.error(t('error_deleting'))
          }

          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  const handleSelectAll = () => {
    if (properties && properties.items.length > 0) {
      if (selectedMessageIds.length === properties.items.length) {
        // If all messages are selected, deselect all
        deselectAllMessages()
      } else {
        // Otherwise, select all messages
        selectAllMessages(properties.items)
      }
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TablePropertiesIcon className="h-5 w-5" />
                {t('properties')}
              </CardTitle>
              <CardDescription>{t('properties_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {canCreate && (
                <Button asChild>
                  <Link href="/products/properties/create">
                    <SquarePlusIcon />
                    <span className="hidden lg:flex">{t('create_property')}</span>
                  </Link>
                </Button>
              )}
              {canDelete && selectedMessageIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedMessageIds.length})</span>
                  </div>
                </Button>
              )}
              <PropertiesFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {properties && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            {canDelete && (
                              <Checkbox
                                checked={
                                  properties &&
                                  properties.items.length > 0 &&
                                  selectedMessageIds.length === properties.items.length
                                }
                                onCheckedChange={() => {
                                  handleSelectAll()
                                }}
                                onClick={e => {
                                  e.stopPropagation()
                                }}
                                className="cursor-pointer"
                              />
                            )}
                          </div>
                        </TableHead>
                        <TableHead className="w-25">
                          <SortableHeader
                            sortBy="id"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <HashIcon className="mr-2 h-4 w-4" />
                            ID
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-75">
                          <SortableHeader
                            sortBy="key"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <KeyIcon className="mr-2 h-4 w-4" />
                            {t('key')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead>
                          <SortableHeader
                            sortBy={`translations.${locale}.name`}
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <div className="flex items-center gap-2">
                              <Flag country={getCountryCodeFromLocale(locale)} size={16} />
                              {t('name')}
                            </div>
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-30 text-center">
                          <SortableHeader
                            sortBy="order"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <ArrowUpRightIcon className="h-4 w-4" />
                            {t('sort_order')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-37.5">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-37.5">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {t('updated_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-20">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {properties.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        properties.items.map((property, index) => (
                          <ContextMenu key={property.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => canShow && handleView(property.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    {canDelete && (
                                      <Checkbox
                                        checked={selectedMessageIds.includes(property.id)}
                                        onCheckedChange={() => toggleMessageSelection(property.id)}
                                        onClick={e => {
                                          e.stopPropagation()
                                        }}
                                        className="cursor-pointer"
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-muted-foreground font-mono text-xs">{property.id}</TableCell>
                                <TableCell>
                                  <div className="truncate">{property.key}</div>
                                </TableCell>
                                <TableCell>
                                  <div className="max-w-75 truncate">
                                    {getLocalizedValue(property, 'name', locale, defaultLocale)}
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="flex flex-row items-center justify-center gap-2">
                                    <span className="text-sm">{property.order}</span>
                                    {canUpdate && (
                                      <TableSortActions
                                        onSortUp={() => handleSortOrder(property.id, SortOrderEnum.UP)}
                                        onSortDown={() => handleSortOrder(property.id, SortOrderEnum.DOWN)}
                                        disabled={isSorting}
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-muted-foreground text-center text-sm">
                                  {formatDateTime(property.createdAt, locale)}
                                </TableCell>
                                <TableCell className="text-muted-foreground text-center text-sm">
                                  {formatDateTime(property.updatedAt, locale)}
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        className="h-8 w-8 p-0"
                                        onClick={e => e.stopPropagation()}>
                                        <span className="sr-only">{t('open_menu')}</span>
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {canShow && (
                                        <DropdownMenuItem
                                          onClick={e => {
                                            e.stopPropagation()
                                            handleView(property.id)
                                          }}>
                                          <EyeIcon className="mr-2 h-4 w-4" />
                                          {t('property_details')}
                                        </DropdownMenuItem>
                                      )}

                                      {canUpdate && (
                                        <DropdownMenuItem
                                          onClick={e => {
                                            e.stopPropagation()
                                            handleEdit(property.id)
                                          }}>
                                          <EditIcon className="mr-2 h-4 w-4" />
                                          {t('edit_property')}
                                        </DropdownMenuItem>
                                      )}

                                      {canDelete && (
                                        <DropdownMenuItem
                                          onClick={e => {
                                            e.stopPropagation()
                                            handleDelete(property.id)
                                          }}
                                          className="text-destructive focus:text-destructive">
                                          <Trash2Icon className="mr-2 h-4 w-4" />
                                          {t('delete')}
                                        </DropdownMenuItem>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>
                              {canShow && (
                                <ContextMenuItem
                                  onClick={e => {
                                    e.stopPropagation()
                                    handleView(property.id)
                                  }}>
                                  <EyeIcon className="mr-2 h-4 w-4" />
                                  {t('property_details')}
                                </ContextMenuItem>
                              )}

                              {canUpdate && (
                                <ContextMenuItem
                                  onClick={e => {
                                    e.stopPropagation()
                                    handleEdit(property.id)
                                  }}>
                                  <EditIcon className="mr-2 h-4 w-4" />
                                  {t('edit_property')}
                                </ContextMenuItem>
                              )}

                              {canDelete && (
                                <ContextMenuItem
                                  onClick={e => {
                                    e.stopPropagation()
                                    handleDelete(property.id)
                                  }}
                                  className="text-destructive focus:text-destructive">
                                  <Trash2Icon className="mr-2 h-4 w-4" />
                                  {t('delete')}
                                </ContextMenuItem>
                              )}
                            </ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={properties.page}
                  totalPages={properties.pages}
                  totalItems={properties.total}
                  pageSize={properties.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      {canDelete && (
        <DeleteConfirmationDialog
          open={deleteMessageId !== null}
          onCancel={() => setDeleteMessageId(null)}
          onConfirm={confirmDeleteMessage}
          isDeleting={isDeleting}
        />
      )}

      {canDelete && (
        <DeleteConfirmationDialog
          open={bulkDeleteConfirmOpen}
          onCancel={() => setBulkDeleteConfirmOpen(false)}
          onConfirm={confirmBulkDeleteMessages}
          isDeleting={isBulkDeleting}
        />
      )}
    </>
  )
}

export default List
